import React from 'react';
import { useIntl } from 'react-intl';
import ReactHtmlParser from 'react-html-parser';
import { BLOCKS } from '@contentful/rich-text-types';

import useLocation from '../../../../utils/location';
import RichText from '../../RichText';
import { MultiBannerItem } from '../../../../ContentfulTypes';
import { ImageLoadingType } from '../../Image/Image';
import isKiosk from '../../../../utils/isKiosk';

import MultiHeroBannerImage from './MultiHeroBannerImage';
import {
  Banner,
  BannerUnlinked,
  ImageWrapper,
  ContentWrapper,
  CtaContainer,
} from './styles';

const bodyOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <div className="descripton">{children}</div>,
  },
};

interface Props {
  data: MultiBannerItem;
  event?: string;
  index?: number;
  bannerCount?: number;
  className: string;
  position?: number;
  $index: number;
  useSharpImage?: boolean;
  imageLoadingType?: ImageLoadingType;
  bladeVisible?: boolean;
  setBladeVisible?: () => void;
}

const MultiHeroBanner = ({
  data,
  event = 'heroBanner',
  index = 0,
  bannerCount = 1,
  className,
  position,
  $index,
  useSharpImage,
  imageLoadingType,
  bladeVisible = false,
  setBladeVisible,
}: Props) => {
  const { locale } = useIntl();
  const { pathName } = useLocation();
  const isKioskMode = isKiosk();

  const onHandleClick = () => {
    window.dataLayer = window.dataLayer || [];
    const gtmData = {
      event,
      heroBannerPromotion: data?.cmsName,
      pageAndPosition: `${pathName} - Position ${event === 'oem_banner' ? $index + 1 : position + 1}`,
    };
    window.dataLayer.push(gtmData);
  };
  const bannerContent = (
    <>
      <ImageWrapper
        $index={index}
        $bannerCount={bannerCount}
        $bgColor={data?.backgroundColorImage}
        $imagePosition={data?.imagePosition}
      >
        <MultiHeroBannerImage
          bannerData={data}
          index={index}
          useSharpImage={useSharpImage}
          imageLoadingType={imageLoadingType}
        />
      </ImageWrapper>
      <ContentWrapper
        $index={index}
        $bannerCount={bannerCount}
        $bgColor={data?.backgroundColorContent}
        $color={data?.displayTextColor}
        $imagePosition={data?.imagePosition}
        className={className}
      >
        {data?.bannerHeading && (<div className="title-heading">{ReactHtmlParser(data.bannerHeading)}</div>)}
        {data?.bannerSubHeading && (<div className="title-subheading">{ReactHtmlParser(data.bannerSubHeading)}</div>)}
        {data?.pointsHeading && (<div className="title-points">{ReactHtmlParser(data.pointsHeading)}</div>)}
        {data?.points && (<div className="points">{data.points}</div>)}
        {data?.bannerBody && (
          <RichText document={data.bannerBody} options={bodyOptions} />
        )}
        {isKioskMode && data?.bannerCta?.ctaHideOption?.includes('KIOSK') ? null : (
          <CtaContainer
            $index={index}
            $bannerCount={bannerCount}
            className={className}
            $imagePosition={data?.imagePosition}
          >
            {/* Custom mock button based on CMS as entire banner is clickable */}
            {data?.bannerCta?.ctaType === 'button' ? (
              <div
                className={`cta-button ${data?.bannerCta?.ctaColor}`}
              >
                {data?.bannerCta?.ctaLabel?.ctaLabel}
              </div>
            ) : (
              <div
                className={`cta-text-link ${data?.bannerCta?.ctaColor}`}
              >
                {data?.bannerCta?.ctaLabel?.ctaLabel}
              </div>
            )}
          </CtaContainer>
        )}
      </ContentWrapper>
    </>
  );

  return (
    <>
      {data?.bannerCta && (
        isKioskMode && data?.bannerCta?.ctaHideOption?.includes('KIOSK') ? (
          <BannerUnlinked
            tabIndex="0"
            key={data?.id}
            $index={index}
            $bannerCount={bannerCount}
            className={className}
          >
            {bannerContent}
          </BannerUnlinked>
        ) : (
          <Banner
            tabIndex="0"
            key={data?.id}
            $index={index}
            $bannerCount={bannerCount}
            $buttonColor={data?.bannerCta?.ctaColor}
            route={data?.bannerCta?.ctaTarget}
            url={data?.bannerCta?.ctaUrl}
            bladeVisible={bladeVisible}
            setBladeVisible={setBladeVisible}
            locale={locale}
            className={className}
            onHandleClick={onHandleClick}
            data-testid="cta-button"
          >
            {bannerContent}
          </Banner>
        )
      )}
      {!data?.bannerCta && (
        <BannerUnlinked
          tabIndex="0"
          key={data?.id}
          $index={index}
          $bannerCount={bannerCount}
          className={className}
        >
          {bannerContent}
        </BannerUnlinked>
      )}
    </>
  );
};

export default MultiHeroBanner;
